import { useEffect, useMemo, useRef, type MutableRefObject } from "react";
import { config } from "../config";
import {
  getOperation,
  listOperations,
  type IOperationResponse,
} from "../services/api";
import { useStore } from "../store/globalState";

const TERMINAL_STATUSES = new Set([
  "completed",
  "failed",
  "cancelled",
]);

export function isOperationNonTerminal(op: IOperationResponse): boolean {
  return !TERMINAL_STATUSES.has(op.status);
}

export function hasNonTerminalOperations(ops: IOperationResponse[]): boolean {
  return ops.some((op) => isOperationNonTerminal(op));
}

export type UseOperationSSEOptions = {
  /** Si se define, solo se abre SSE mientras esa operación exista en el store y no sea terminal. */
  watchOperationId?: string;
};

function closeEventSource(
  es: EventSource | null,
  esRef: MutableRefObject<EventSource | null>,
): void {
  if (!es) return;
  es.close();
  if (esRef.current === es) {
    esRef.current = null;
  }
}

/**
 * SSE solo mientras haya operaciones no finales (listado) o mientras la operación observada no sea final (detalle).
 * Tras cada evento y cada heartbeat se revalida y se cierra si ya no aplica.
 */
export function useOperationSSE(
  linkId: string | null | undefined,
  options?: UseOperationSSEOptions,
): void {
  const watchOperationId = options?.watchOperationId;
  const operations = useStore((s) => s.operations);
  const setOperations = useStore((s) => s.setOperations);
  const esRef = useRef<EventSource | null>(null);
  const isFirstOpenRef = useRef(true);

  const hasActiveOps = useMemo(() => {
    if (!watchOperationId) {
      return hasNonTerminalOperations(operations);
    }
    const op = operations.find((o) => o.operationId === watchOperationId);
    return op ? isOperationNonTerminal(op) : false;
  }, [operations, watchOperationId]);

  useEffect(() => {
    if (!linkId) {
      closeEventSource(esRef.current, esRef);
      return;
    }

    if (!hasActiveOps) {
      closeEventSource(esRef.current, esRef);
      return;
    }

    if (esRef.current) {
      return;
    }

    isFirstOpenRef.current = true;
    const url = `${config.API_BASE_URL}/widget/links/${linkId}/operations/sse`;
    const es = new EventSource(url);
    esRef.current = es;

    const maybeClose = (): void => {
      const { operations: ops } = useStore.getState();
      if (watchOperationId) {
        const op = ops.find((o) => o.operationId === watchOperationId);
        if (!op || !isOperationNonTerminal(op)) {
          closeEventSource(es, esRef);
        }
        return;
      }
      if (!hasNonTerminalOperations(ops)) {
        closeEventSource(es, esRef);
      }
    };

    es.onopen = () => {
      if (!isFirstOpenRef.current) {
        if (watchOperationId) {
          void getOperation(linkId, watchOperationId)
            .then((fetched) => {
              const { operations: current } = useStore.getState();
              const idx = current.findIndex(
                (o) => o.operationId === fetched.operationId,
              );
              if (idx >= 0) {
                const next = [...current];
                next[idx] = fetched;
                setOperations(next);
              } else {
                setOperations([fetched, ...current]);
              }
            })
            .catch((err) =>
              console.error(
                "SSE reconnect: failed to refresh watched operation",
                err,
              ),
            );
        } else {
          void listOperations(linkId)
            .then((ops) => setOperations(ops))
            .catch((err) =>
              console.error("SSE reconnect: failed to refresh operations", err),
            );
        }
      }
      isFirstOpenRef.current = false;
    };

    es.onmessage = (event: MessageEvent) => {
      try {
        const raw = event.data as string;
        const data = typeof raw === "string" ? JSON.parse(raw) : raw;
        if (data?.type === "heartbeat") {
          maybeClose();
          return;
        }

        const updated = data as IOperationResponse;
        if (!updated?.operationId) return;

        const { operations: current } = useStore.getState();
        const idx = current.findIndex((o) => o.operationId === updated.operationId);
        if (idx >= 0) {
          const next = [...current];
          next[idx] = updated;
          setOperations(next);
        } else {
          setOperations([updated, ...current]);
        }

        maybeClose();
      } catch (e) {
        console.error("SSE message parse error", e);
      }
    };

    es.onerror = () => {
      console.warn("SSE connection issue (auto-retry)", linkId);
    };

    return () => {
      closeEventSource(es, esRef);
    };
  }, [linkId, hasActiveOps, setOperations, watchOperationId]);
}
